import axios from "axios";

export const sendAppointmentInvite = async (
  token: string,
  params: {
    emails: string[];
    patientName: string;
    patientGender?: number;
    doctorName: string;
    doctorGender?: number;
    scheduledDate: string;
  }
) => {
  const {
    emails,
    patientName,
    patientGender,
    doctorName,
    doctorGender,
    scheduledDate
  } = params;
  const id = crypto.randomUUID().replace(/-/g, "_");

  await Promise.all(
    emails.map(email =>
      axios.post(
        `${process.env.REACT_APP_API_URL}/api/video/notify`,
        {
          email,
          id,
          patient: { name: patientName, gender: patientGender },
          doctor: { name: doctorName, gender: doctorGender },
          scheduledDate,
          scheduledDuration: "30m"
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    )
  );
};

// The notify-cancel endpoint expects the gender as a string ("male"/"female"),
// unlike the numeric codes used elsewhere in the app (1 = male, 2 = female).
const genderToLabel = (gender?: number): "male" | "female" | undefined =>
  gender === 1 ? "male" : gender === 2 ? "female" : undefined;

/**
 * Notify recipients that a scheduled appointment was cancelled. Mirrors
 * `sendAppointmentInvite` but hits `/notify-cancel` (same base URL family).
 */
export const sendAppointmentCancelNotice = async (
  token: string,
  params: {
    emails: string[];
    patientName: string;
    patientGender?: number;
    doctorName: string;
    doctorGender?: number;
    scheduledDate: string;
  }
) => {
  const {
    emails,
    patientName,
    patientGender,
    doctorName,
    doctorGender,
    scheduledDate
  } = params;

  const payloadBase = {
    patient: { name: patientName, gender: genderToLabel(patientGender) },
    doctor: { name: doctorName, gender: genderToLabel(doctorGender) },
    scheduledDate
  };

  // One request per recipient, each with a singular `email` field (matching
  // the mobile client), never an `emails` array.
  await Promise.all(
    emails.map(email =>
      axios.post(
        `${process.env.REACT_APP_API_URL}/api/video/notify-cancel`,
        { email, ...payloadBase },
        { headers: { Authorization: `Bearer ${token}` } }
      )
    )
  );
};

/**
 * Discriminates why a cancellation failed, so the UI can show a tailored
 * message. Maps to the backend status codes:
 *  - `cannotCancel` → 400 (the appointment exists but can't be cancelled)
 *  - `notFound`     → 404 (no matching entry for this token)
 *  - `server`       → 5xx (internal server error)
 *  - `generic`      → anything else (network error, unexpected status)
 */
export type CancelVideoErrorKind =
  | "cannotCancel"
  | "notFound"
  | "server"
  | "generic";

export class CancelVideoError extends Error {
  kind: CancelVideoErrorKind;
  constructor(kind: CancelVideoErrorKind) {
    super(kind);
    this.name = "CancelVideoError";
    this.kind = kind;
  }
}

/**
 * Cancels an appointment straight from the patient's email link — no auth. The
 * token is an opaque, unguessable identifier the backend generates per
 * appointment and embeds in the link; the backend validates it and cancels the
 * matching appointment. Resolves on 200; throws a `CancelVideoError` carrying
 * the reason otherwise.
 */
export const cancelVideoByToken = async (cancelToken: string) => {
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/video/cancel/${cancelToken}`
    );
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === 400) throw new CancelVideoError("cannotCancel");
    if (status === 404) throw new CancelVideoError("notFound");
    if (status && status >= 500) throw new CancelVideoError("server");
    throw new CancelVideoError("generic");
  }
};
